import type { PGliteInterface, Transaction } from '@electric-sql/pglite';
import { newId, TemplateDoc, templateSearchText } from '@quickreport/domain';
import { queueChange } from '@quickreport/sync';

export {
  type TemplateListItem,
  templateCountQuery,
  templateListQuery,
} from '@quickreport/workspace';

type Q = Pick<PGliteInterface, 'query'> | Transaction;

export const searchTextOf = templateSearchText;

export async function loadTemplate(
  db: Q,
  id: string,
): Promise<{ doc: TemplateDoc; version: number } | null> {
  const r = await db.query<{ doc: unknown; version: number }>(
    `select v.doc, v.version from templates t join template_versions v
       on v.template_id = t.id and v.version = t.current_version where t.id = $1`,
    [id],
  );
  const row = r.rows[0];
  return row ? { doc: TemplateDoc.parse(row.doc), version: row.version } : null;
}

/**
 * Save a template as a new immutable version. Reports pin the version they were made with,
 * so editing a template never changes an existing report. Queues the change for sync.
 */
export async function saveTemplateVersion(
  tx: Q,
  orgId: string,
  templateId: string | null,
  input: TemplateDoc,
): Promise<{ id: string; version: number }> {
  const doc = TemplateDoc.parse(input);
  const id = templateId ?? newId();
  let version = 1;
  if (templateId) {
    const cur = await tx.query<{ current_version: number }>(
      'select current_version from templates where id = $1',
      [id],
    );
    version = (cur.rows[0]?.current_version ?? 0) + 1;
    await tx.query(
      `update templates set name = $2, department = $3, search_text = $4, sensitive = $5,
         current_version = $6, updated_at = now() where id = $1`,
      [id, doc.name, doc.department ?? null, searchTextOf(doc), doc.sensitive, version],
    );
  } else {
    await tx.query(
      `insert into templates (id, org_id, name, department, search_text, sensitive, current_version)
       values ($1, $2, $3, $4, $5, $6, 1)`,
      [id, orgId, doc.name, doc.department ?? null, searchTextOf(doc), doc.sensitive],
    );
  }
  const versionId = newId();
  await tx.query(
    'insert into template_versions (id, org_id, template_id, version, doc) values ($1, $2, $3, $4, $5)',
    [versionId, orgId, id, version, doc],
  );
  await queueChange(tx, 'templates', id);
  await queueChange(tx, 'template_versions', versionId);
  return { id, version };
}

/** A copy under a new name, to adapt for another analyzer or age group. */
export async function duplicateTemplate(
  db: Pick<PGliteInterface, 'transaction'>,
  orgId: string,
  id: string,
): Promise<string> {
  return db.transaction(async (tx) => {
    const cur = await loadTemplate(tx, id);
    if (!cur) throw new Error('That template is no longer here.');
    const { id: copyId } = await saveTemplateVersion(tx, orgId, null, {
      ...cur.doc,
      name: `${cur.doc.name} (copy)`,
    });
    return copyId;
  });
}

/** Hide a template from new reports. Reports already made with it are not touched. */
export async function archiveTemplate(
  db: Pick<PGliteInterface, 'transaction'>,
  id: string,
): Promise<void> {
  await db.transaction(async (tx) => {
    await tx.query('update templates set archived_at = now(), updated_at = now() where id = $1', [
      id,
    ]);
    await queueChange(tx, 'templates', id);
  });
}
